import { useState } from 'react';
import { ArrowLeftIcon } from '@phosphor-icons/react';
import { DatePicker } from '@/components/date-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { JourneyReport } from '@/components/reports/journey-report';
import { SaveJourneyReportModal } from '@/components/reports/save-journey-report-modal';
import { useEventNames } from '@/hooks/use-event-names';
import { PageHeader } from '@/components/page-header';
import { PageContent } from '@/components/page-content';
import { hasPermission } from '@/lib/permissions';
import type { JourneyAnchor, JourneyReportConfig } from '@/types';

type Direction = 'forward' | 'backward';

// Prefill params from the funnel "View as Journey" bridge (FR-22).
type JourneySearch = {
  anchorType?: 'Event' | 'Pageview';
  anchorValue?: string;
  direction?: Direction;
};

export const Route = createFileRoute(
  '/_auth/_side-layout/journeys_/add-journey-report',
)({
  component: AddJourneyReport,
  validateSearch: (search: Record<string, unknown>): JourneySearch => ({
    anchorType:
      search.anchorType === 'Pageview'
        ? 'Pageview'
        : search.anchorType === 'Event'
          ? 'Event'
          : undefined,
    anchorValue:
      typeof search.anchorValue === 'string' ? search.anchorValue : undefined,
    direction:
      search.direction === 'backward'
        ? 'backward'
        : search.direction === 'forward'
          ? 'forward'
          : undefined,
  }),
  beforeLoad: async ({ context }) => {
    if (!hasPermission(context.userRole, { project: ['update'] })) {
      throw redirect({ to: '/journeys' });
    }
  },
});

const DEPTHS = [1, 2, 3, 4, 5, 6, 7, 8];
const TOP_KS = [3, 5, 8, 10];
const MIN_COUNTS = [1, 2, 5, 10];

function AddJourneyReport() {
  // Prefill from the funnel "View as Journey" bridge (FR-22).
  const search = Route.useSearch();

  const allNames: string[] = useEventNames() ?? [];
  const names = allNames.filter((n) => n !== 'Pageview');
  const [direction, setDirection] = useState<Direction>(
    search.direction ?? 'forward',
  );
  const [anchorType, setAnchorType] = useState<JourneyAnchor['type']>(
    search.anchorType ?? 'Event',
  );
  const [anchorValue, setAnchorValue] = useState(search.anchorValue ?? '');
  const [depth, setDepth] = useState(3);
  const [topK, setTopK] = useState(3);
  const [minCount, setMinCount] = useState(1);
  const [excludeEvents, setExcludeEvents] = useState<string[]>([]);

  const config: JourneyReportConfig = {
    type: 'journey',
    anchor: { type: anchorType, value: anchorValue },
    direction,
    depth,
    topK,
    minCount,
    excludeEvents: excludeEvents.length ? excludeEvents : undefined,
  };

  const anchorNoun = anchorType === 'Pageview' ? 'Page' : 'Event';
  const anchorVerb = direction === 'backward' ? 'Ending' : 'Starting';

  return (
    <>
      <PageHeader
        title="Add Journey Report"
        description="Pick an anchor event or page and explore the paths around it."
      />
      <PageContent>
        <div className="flex items-center gap-3">
          <Link to={'/journeys' as string}>
            <Button iconLeft={<ArrowLeftIcon />} variant="outline">
              Back
            </Button>
          </Link>
          <DatePicker />
          {!!anchorValue && <SaveJourneyReportModal config={config} />}
        </div>

        <div className="border-border grid grid-cols-1 gap-5 rounded border p-5 sm:grid-cols-2 lg:grid-cols-6">
          <div className="flex flex-col gap-2">
            <Label>Direction</Label>
            <Select
              value={direction}
              onValueChange={(val) => setDirection(val as Direction)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="forward">Forward (after)</SelectItem>
                <SelectItem value="backward">Backward (before)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Anchor Type</Label>
            <Select
              value={anchorType}
              onValueChange={(val) => {
                setAnchorType(val as JourneyAnchor['type']);
                setAnchorValue('');
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Event">Event</SelectItem>
                <SelectItem value="Pageview">Pageview</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>
              {anchorVerb} {anchorNoun}
            </Label>
            {anchorType === 'Pageview' ? (
              <Input
                placeholder="/pricing"
                value={anchorValue}
                onChange={(e) => setAnchorValue(e.target.value)}
              />
            ) : (
              <Select value={anchorValue} onValueChange={setAnchorValue}>
                <SelectTrigger>
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent>
                  {names.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label>Steps</Label>
            <Select
              value={String(depth)}
              onValueChange={(val) => setDepth(Number(val))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DEPTHS.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Branches per step</Label>
            <Select
              value={String(topK)}
              onValueChange={(val) => setTopK(Number(val))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TOP_KS.map((k) => (
                  <SelectItem key={k} value={String(k)}>
                    {k}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>Minimum sessions</Label>
            <Select
              value={String(minCount)}
              onValueChange={(val) => setMinCount(Number(val))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MIN_COUNTS.map((n) => (
                  <SelectItem key={n} value={String(n)}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2 sm:col-span-2 lg:col-span-6">
            <Label>Exclude events (optional)</Label>
            <MultiSelect
              options={allNames.map((name) => ({ value: name, label: name }))}
              value={excludeEvents}
              onChange={setExcludeEvents}
              placeholder="Hide noisy events from the journey…"
            />
          </div>
        </div>

        {anchorValue ? (
          <JourneyReport config={config} />
        ) : (
          <p className="border-border w-full rounded-lg border p-10 text-center text-gray-500">
            Choose an anchor event or page to preview the journey.
          </p>
        )}
      </PageContent>
    </>
  );
}
